const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const XLINK_NAMESPACE = 'http://www.w3.org/1999/xlink';

const ALLOWED_ELEMENTS: ReadonlySet<string> = new Set([
	'svg', 'g', 'defs', 'symbol', 'use', 'title', 'desc', 'switch',
	'text', 'tspan', 'textPath',
	'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'path', 'image',
	'clipPath', 'mask', 'pattern', 'linearGradient', 'radialGradient', 'stop', 'marker'
]);

const ALLOWED_REFERENCE_ATTRIBUTES: ReadonlySet<string> = new Set(['href', 'xlink:href']);

const IMAGE_DATA_URL_PATTERN = /^data:image\/(png|jpe?g|gif|webp);base64,[a-z0-9+/=\s]*$/i;
const FRAGMENT_REFERENCE_PATTERN = /^#[\w.:-]+$/;
const UNSAFE_STYLE_PATTERN = /url\s*\(|expression\s*\(|javascript:/i;

export class SvgSanitizer {
	/**
	 * Parses SVG markup and rebuilds it as a detached DOM tree containing only
	 * allow-listed elements and attributes. Script-capable content (script,
	 * foreignObject, animation, event handlers, non-image URL references) is dropped.
	 */
	public static sanitizeToFragment(markup: string, targetDocument: Document = document): DocumentFragment {
		const fragment = targetDocument.createDocumentFragment();
		if (!markup || !markup.trim()) {
			return fragment;
		}

		const parsed = new DOMParser().parseFromString(markup, 'image/svg+xml');
		if (parsed.getElementsByTagName('parsererror').length > 0) {
			return fragment;
		}

		Array.from(parsed.childNodes).forEach(node => {
			const sanitized = SvgSanitizer.sanitizeNode(node, targetDocument);
			if (sanitized) {
				fragment.appendChild(sanitized);
			}
		});
		return fragment;
	}

	/**
	 * XML-encodes a value so it can only ever be interpreted as character data,
	 * never as markup, once substituted into an SVG template.
	 */
	public static escapeXml(value: string): string {
		return value
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&apos;');
	}

	/**
	 * Returns a copy of a substitution map with every string value XML-encoded.
	 * Keys listed in `markupKeys` hold application-generated markup and are left as-is;
	 * that markup is still sanitized at the rendering sink.
	 */
	public static escapeSubstitutionValues<T extends object>(data: T, markupKeys: string[] = []): T {
		const escaped: any = Array.isArray(data) ? [...(data as any)] : { ...(data as any) };
		Object.keys(escaped).forEach(key => {
			if (markupKeys.indexOf(key) !== -1) {
				return;
			}
			const value = escaped[key];
			if (typeof value === 'string') {
				escaped[key] = SvgSanitizer.escapeXml(value);
			}
		});
		return escaped as T;
	}

	/**
	 * Returns the value when it is a base64 image data URL, and an empty string otherwise.
	 */
	public static sanitizeImageDataUrl(value: string): string {
		return value && IMAGE_DATA_URL_PATTERN.test(value.trim()) ? value.trim() : '';
	}

	private static sanitizeNode(node: Node, targetDocument: Document): Node {
		if (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.CDATA_SECTION_NODE) {
			return targetDocument.createTextNode(node.nodeValue || '');
		}
		if (node.nodeType !== Node.ELEMENT_NODE) {
			return null;
		}

		const element = node as Element;
		if (!ALLOWED_ELEMENTS.has(element.localName)) {
			return null;
		}

		const sanitized = targetDocument.createElementNS(element.namespaceURI || SVG_NAMESPACE, element.localName);
		Array.from(element.attributes).forEach(attribute => {
			if (SvgSanitizer.isAllowedAttribute(attribute)) {
				if (attribute.namespaceURI) {
					sanitized.setAttributeNS(attribute.namespaceURI, attribute.name, attribute.value);
				} else {
					sanitized.setAttribute(attribute.name, attribute.value);
				}
			}
		});

		Array.from(element.childNodes).forEach(child => {
			const sanitizedChild = SvgSanitizer.sanitizeNode(child, targetDocument);
			if (sanitizedChild) {
				sanitized.appendChild(sanitizedChild);
			}
		});
		return sanitized;
	}

	private static isAllowedAttribute(attribute: Attr): boolean {
		const name = attribute.name.toLowerCase();
		const value = attribute.value || '';

		if (name.indexOf('on') === 0) {
			return false;
		}
		if (name === 'style') {
			return !UNSAFE_STYLE_PATTERN.test(value);
		}
		if (ALLOWED_REFERENCE_ATTRIBUTES.has(name) || attribute.namespaceURI === XLINK_NAMESPACE) {
			const reference = value.trim();
			return FRAGMENT_REFERENCE_PATTERN.test(reference) || IMAGE_DATA_URL_PATTERN.test(reference);
		}
		return true;
	}
}
